import { useState } from "react";
import { toast } from "sonner";
import { Edit3, Trash2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const ROUTES = [
  { id: "tor-cal", name: "Toronto → California" },
  { id: "cal-mex", name: "California → Mexico" },
  { id: "tor-mex", name: "Toronto → Mexico" },
];
const SHIP_STATUS = ["In Transit", "At Border", "Customs Cleared", "Out for Delivery", "Delivered"];
const emptyShip = { tracking_number: "", customer_email: "", route_id: "tor-cal", current_index: 0, status: "In Transit", cargo: "", temp: "Ambient", eta: "", driver_phone: "", driver_name: "", driver_pin: "" };

export default function AdminShipmentsTab({ ships, reload }) {
  const [ship, setShip] = useState(emptyShip);
  const [editingShip, setEditingShip] = useState(false);

  const saveShip = async () => {
    try {
      const body = { ...ship, current_index: Number(ship.current_index) };
      if (editingShip) await api.put(`/admin/shipments/${ship.tracking_number}`, body);
      else await api.post("/admin/shipments", body);
      toast.success("Shipment saved."); setShip(emptyShip); setEditingShip(false); reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed."); }
  };
  const editShip = (s) => { setShip({ tracking_number: s.tracking_number, customer_email: s.customer_email, route_id: s.route_id, current_index: s.current_index, status: s.status, cargo: s.cargo, temp: s.temp, eta: s.eta || "", driver_phone: s.driver_phone || "", driver_name: s.driver_name || "", driver_pin: "" }); setEditingShip(true); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const delShip = async (tn) => { await api.delete(`/admin/shipments/${tn}`); toast.success("Deleted."); reload(); };

  return (
    <div className="space-y-8">
      <div className="rounded-2xl border border-border glass p-6">
        <h3 className="font-heading font-bold text-lg mb-4">{editingShip ? `Edit ${ship.tracking_number}` : "New shipment"}</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <Input data-testid="ship-tracking" placeholder="Tracking # (e.g. CR-TOR-CAL-9001)" value={ship.tracking_number} disabled={editingShip} onChange={(e) => setShip({ ...ship, tracking_number: e.target.value })} />
          <Input data-testid="ship-email" placeholder="Customer email" value={ship.customer_email} onChange={(e) => setShip({ ...ship, customer_email: e.target.value })} />
          <Select value={ship.route_id} onValueChange={(v) => setShip({ ...ship, route_id: v })}>
            <SelectTrigger data-testid="ship-route"><SelectValue /></SelectTrigger>
            <SelectContent>{ROUTES.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={ship.status} onValueChange={(v) => setShip({ ...ship, status: v })}>
            <SelectTrigger data-testid="ship-status"><SelectValue /></SelectTrigger>
            <SelectContent>{SHIP_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Input data-testid="ship-index" type="number" placeholder="Current stop index (0-5)" value={ship.current_index} onChange={(e) => setShip({ ...ship, current_index: e.target.value })} />
          <Input placeholder="Temp (e.g. 2°C / Ambient)" value={ship.temp} onChange={(e) => setShip({ ...ship, temp: e.target.value })} />
          <Input placeholder="Cargo" value={ship.cargo} onChange={(e) => setShip({ ...ship, cargo: e.target.value })} />
          <Input placeholder="ETA (e.g. Jul 08, 2026 · 14:00)" value={ship.eta} onChange={(e) => setShip({ ...ship, eta: e.target.value })} />
          <Input data-testid="ship-driver-name" placeholder="Driver name (optional)" value={ship.driver_name} onChange={(e) => setShip({ ...ship, driver_name: e.target.value })} />
          <Input data-testid="ship-driver-phone" placeholder="Driver phone (e.g. +1 437 808 2496)" value={ship.driver_phone} onChange={(e) => setShip({ ...ship, driver_phone: e.target.value })} />
          <Input data-testid="ship-driver-pin" placeholder="Driver access code / PIN (set or change)" value={ship.driver_pin} onChange={(e) => setShip({ ...ship, driver_pin: e.target.value })} />
        </div>
        <div className="mt-4 flex gap-3">
          <button data-testid="ship-save" onClick={saveShip} className="rounded-full bg-primary text-primary-foreground font-semibold px-6 py-2.5 text-sm">{editingShip ? "Update" : "Create"} shipment</button>
          {editingShip && <button onClick={() => { setShip(emptyShip); setEditingShip(false); }} className="rounded-full border border-border px-6 py-2.5 text-sm">Cancel</button>}
        </div>
      </div>
      <div className="rounded-2xl border border-border overflow-hidden">
        {ships.map((s) => (
          <div key={s.tracking_number} data-testid={`ship-row-${s.tracking_number}`} className="flex items-center justify-between px-5 py-4 border-b border-border last:border-0">
            <div>
              <div className="font-mono-x font-semibold">{s.tracking_number}</div>
              <div className="text-sm text-muted-foreground">{s.customer_email} · {s.status} · stop {s.current_index}</div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => editShip(s)} className="w-9 h-9 rounded-lg border border-border flex items-center justify-center hover:bg-secondary"><Edit3 className="w-4 h-4" /></button>
              <button data-testid={`ship-del-${s.tracking_number}`} onClick={() => delShip(s.tracking_number)} className="w-9 h-9 rounded-lg border border-border flex items-center justify-center hover:bg-red-500/10 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
